<!-- Thumbnail gallery of videos, filtered from assets/videos/portfolio.json by collection -->

<template>
  <div class="gallery">
    <div v-for="video in videos" :key="video.url" class="portrait">
      <a :href="video.url" class="thumbnail" @click="openVideo($event, video)">
        <img :src="video.image" :alt="video.title" />
        <img :src="playButton" alt="" class="play" />
      </a>
      <p>
        <strong>{{ video.title }}</strong>
        {{ video.text }}
        <template v-if="video.credits?.length">
          <em
            class="credit"
          ><template v-for="(person, index) in video.credits" :key="person.name"
          ><template v-if="index > 0"> & </template
          ><a v-if="person.url" :href="person.url" target="_blank" rel="noopener noreferrer">{{
            person.name
          }}</a
          ><template v-else>{{ person.name }}</template></template
          ><span v-if="video.affiliation" class="affiliation"> ({{ video.affiliation }})</span></em
          >.
        </template>
      </p>
    </div>
  </div>
  <div ref="playerHost" class="player-host">
    <VideoPlayer
      v-if="selectedVideo"
      :key="selectedVideo.url"
      :video="selectedVideo"
      :is-fullscreen="isPlayerFullscreen"
      @hide="closeVideo"
      @ended="playNext"
    />
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import portfolio from "@/assets/videos/portfolio.json";
import playButton from "@/assets/videos/play-button.png";
import VideoPlayer from "@/components/VideoPlayer.vue";
import {getVideoId} from "@/composables/useVideoSource";
import {
  exitFullscreen,
  isFullscreen,
  requestFullscreen,
  useFullscreenExit
} from "@/composables/useFullscreen";
const props = defineProps({
  collection: {type: String, required: true},
  isSortedByYear: {type: Boolean, default: false},
  isLooping: {type: Boolean, default: false} // each video, when it ends, gives way to the next
});
const getVideos = () => {
  const videos = portfolio.filter((video) => video.collections.includes(props.collection));
  if (!props.isSortedByYear) return videos;
  return videos.sort((first, second) => second.year - first.year);
};
const isOpeningElsewhere = (event) => event.metaKey || event.ctrlKey || event.shiftKey;
const route = useRoute();
const router = useRouter();
const videos = getVideos();
const playerHost = ref(null);
const isPlayerFullscreen = ref(false);
const selectedVideo = computed(() => {
  if (!route.params.videoId) return null;
  return videos.find((video) => getVideoId(video.url) === route.params.videoId) ?? null;
});
const openVideo = async (event, video) => {
  if (isOpeningElsewhere(event)) return;
  event.preventDefault();
  isPlayerFullscreen.value = await requestFullscreen(playerHost.value);
  router.push({name: route.name, params: {videoId: getVideoId(video.url)}});
};
const returnToGallery = () => {
  isPlayerFullscreen.value = false;
  if (selectedVideo.value) router.push({name: route.name});
};
// in fullscreen, leaving fullscreen closes the video (see useFullscreenExit below)
const closeVideo = () => {
  if (isFullscreen()) return exitFullscreen();
  returnToGallery();
};
// looping (2026-10-14, Collective): the next video in the page's order, back to the first after the
// last; the player is swapped inside the same host, so fullscreen stays on
const playNext = () => {
  if (!props.isLooping || !selectedVideo.value) return;
  const index = videos.indexOf(selectedVideo.value);
  const next = videos[(index + 1) % videos.length];
  router.replace({name: route.name, params: {videoId: getVideoId(next.url)}});
};
useFullscreenExit(returnToGallery);
</script>

<style scoped>
.player-host:fullscreen {
  background-color: black;
}
.gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem 3%;
}
.portrait {
  width: 30%;
  min-width: 200px;
}
.narrow .portrait {
  width: 100%;
}
.thumbnail {
  position: relative;
  display: block;
  aspect-ratio: 16 / 9;
  border-radius: 4px;
  overflow: hidden;
}
.thumbnail img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumbnail img.play {
  inset: auto;
  top: 50%;
  left: 50%;
  width: 28%;
  height: auto;
  transform: translate(-50%, -50%);
}
.portrait p {
  font-size: 0.9rem;
}
.credit {
  font-size: 0.9em;
}
.affiliation {
  color: var(--text-color-subtle);
}
</style>
